import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import dealsService, { DealWithOffers } from '../api/services/deals';
import { formatUsd } from '../utils/vehicleComparison';

const statusChip = (deal: DealWithOffers) => {
  if (deal.status === 'ACCEPTED')
    return <Chip size="small" color="success" label="Accepted" />;
  if (new Date(deal.offerExpDate) < new Date())
    return <Chip size="small" label="Closed" />;
  if (new Date(deal.initialWindowEnd) > new Date())
    return <Chip size="small" color="primary" label="Collecting offers" />;
  return <Chip size="small" color="warning" label="Dealers competing" />;
};

const Deals = () => {
  const [deals, setDeals] = useState<DealWithOffers[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dealsService
      .find()
      .then(setDeals)
      .catch((e) =>
        setError(e?.response?.data?.message || 'Unable to load your deals.')
      );
  }, []);

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" fontWeight={600}>
          My Deals
        </Typography>
        <Button component={RouterLink} to="/" variant="contained">
          New search
        </Button>
      </Stack>

      {!deals && <Skeleton variant="rectangular" height={200} />}

      {deals?.length === 0 && (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            You haven&apos;t requested any offers yet.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Search for the vehicle you want, pick up to five listings, and let
            the dealers compete.
          </Typography>
          <Button component={RouterLink} to="/" variant="contained">
            Start a search
          </Button>
        </Card>
      )}

      <Stack spacing={2}>
        {deals?.map((deal) => {
          const first = deal.offers[0]?.vehicle;
          const priced = deal.offers.filter((o) => o.price);
          const best = priced.length
            ? Math.min(...priced.map((o) => o.price as number))
            : null;
          return (
            <Card key={deal.id} sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="h6">
                      {first
                        ? `${first.year} ${first.make} ${first.model}`
                        : 'Deal'}
                    </Typography>
                    {statusChip(deal)}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {deal.offers.length} dealer
                    {deal.offers.length === 1 ? '' : 's'} asked ·{' '}
                    {priced.length} responded
                    {best !== null && ` · best ${formatUsd(best)}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Started {new Date(deal.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Button
                  component={RouterLink}
                  to={`/deals/${deal.id}`}
                  variant="outlined"
                  sx={{ alignSelf: 'center' }}
                >
                  View offers
                </Button>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </Container>
  );
};

export default Deals;
